import { DescriptionsItemType } from 'antd/es/descriptions';

import { CompliancePermissionLevelPerStatus } from '@pmp/api';
import { t } from 'i18next';

type ProblemDetails = Record<string, unknown>;

const parseProblem = (error: unknown, depth = 0): ProblemDetails | undefined => {
  if (depth > 4 || error == null) return undefined;
  if (typeof error === 'string') {
    const s = error.trim();
    if (!s.startsWith('{')) return undefined;
    try {
      return JSON.parse(s) as ProblemDetails;
    } catch {
      return undefined;
    }
  }
  if (typeof error !== 'object') return undefined;
  const rec = error as ProblemDetails;
  if ('invalid-params' in rec || 'errors' in rec || 'detail' in rec || 'status' in rec) return rec;
  // rec.message covers an Error wrapping the raw response text.
  for (const nested of [rec.error, rec.data, rec.response, rec.body, rec.message]) {
    const found = parseProblem(nested, depth + 1);
    if (found) return found;
  }
  return undefined;
};

// Direct-CRUD endpoints throw an HTTP validation body (not the CR flow's {isValid,errors});
// map its fields to enso inline errors. Server fields are PascalCase, form fields camelCase.
export const toCrudFieldErrors = (error: unknown): Record<string, string[]> | undefined => {
  const pd = parseProblem(error);
  if (!pd) return undefined;
  const out: Record<string, string[]> = {};
  const toField = (k: string) => k.charAt(0).toLowerCase() + k.slice(1);
  const push = (field: string, msgs: string[]) => {
    if (msgs.length) out[field] = [...(out[field] ?? []), ...msgs];
  };

  const params = pd['invalid-params'];
  if (Array.isArray(params)) {
    for (const p of params as Array<{ name?: string; reason?: string }>) {
      if (p?.name && p?.reason) push(toField(p.name), [p.reason]);
    }
  }
  const errors = pd.errors;
  if (errors && typeof errors === 'object') {
    for (const [k, v] of Object.entries(errors as Record<string, unknown>)) {
      push(toField(k), (Array.isArray(v) ? v : [v]).filter((m): m is string => typeof m === 'string'));
    }
  }
  return Object.keys(out).length ? out : undefined;
};

export const getComplianceItems = (data?: Array<CompliancePermissionLevelPerStatus> | null): DescriptionsItemType[] => {
  if (!data) return [];
  return data.map(({ complianceStatus, permissionLevel }) => {
    return {
      key: complianceStatus ?? '',
      label: t(`lender.lender.labels.${complianceStatus ?? ''}`),
      children: t(`lender.lender.labels.${permissionLevel ?? ''}`),
    };
  });
};
